// frontend/src/features/agency/ReservedDriverList.js
// Phase 8.7C: Agency Reserved lane
//
// Key principles:
// - Membership is assignment-derived (requested/confirmed assignments), NOT driver.status.
// - We render from local `rows` (source of truth for this lane).
// - We optionally upsert into driverSlice as an entity cache.
// - This component does NOT subscribe to socket events anymore.
// - This component does NOT bump statsRefreshKey anymore.
//   Real-time invalidation + stats bump are owned by services/socket.js (Option A/2).

import React, { useMemo, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { upsertDrivers, upsertDriver, removeDriver } from '../../features/drivers/driverSlice';
import { selectQuery, selectStatsRefreshKey } from '../../features/ui/filtersSlice';

import { fetchAgencyReservedDrivers } from '../../services/driverService';

import axios from 'axios';

import DriverForm from '../../features/drivers/DriverForm';
import DriverEditModal from '../../features/drivers/DriverEditModal';
import { RefreshCcw, Trash2, Edit2 } from 'lucide-react';

import { selectAgencyIdForUser } from '../auth/authSlice';

function filterRowsByQuery(rows, q) {
  const needle = (q ?? '').trim().toLowerCase();
  if (!needle) return rows || [];

  return (rows || []).filter((row) => {
    const haystacks = [
      row?.name,
      row?.vehicle_registration_number,
      row?.assignment_status,
      row?.assignment_id != null ? String(row.assignment_id) : null,
      row?.ban_reason,
      row?.reason,
      row?.ban_scope,
      row?.ban_site_id != null ? String(row.ban_site_id) : null,
      row?.ban_id != null ? String(row.ban_id) : null,
    ];

    return haystacks.some((v) => String(v ?? '').toLowerCase().includes(needle));
  });
}


export default function ReservedDriverList() {
  const dispatch = useDispatch();
  const agencyId = useSelector(selectAgencyIdForUser);
  const statsKey = useSelector(selectStatsRefreshKey);
  const q = useSelector(selectQuery);


  // Local membership (authoritative for this lane)
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  // UI state
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  async function reload() {
    const list = await fetchAgencyReservedDrivers();
    setRows(list);

    // Optional: keep entity cache warm for edit modal / details
    dispatch(upsertDrivers(list));
  }

  // Initial load + socket-driven invalidation (via statsKey)
  useEffect(() => {
    if (!agencyId) return;

    (async () => {
      try {
        await reload();
      } finally {
        setLoading(false);
      }
    })();
  }, [agencyId, statsKey]);

  const filtered = useMemo(() => filterRowsByQuery(rows, q), [rows, q]);
  const sorted = useMemo(() => [...filtered], [filtered]);


  // Reserve (create) — NOTE:
  // In the final model "reserve" should create an assignment, not flip driver.status.
  // For now, you can still create drivers here (they'll likely land in Available).
  const handleCreate = async (formData) => {
    try {
      const res = await axios.post('/agencies/me/drivers', formData);
      const newDriver = res.data;

      dispatch(upsertDriver(newDriver));
      setShowAddModal(false);

      // Reserved membership likely won't change unless an assignment is created,
      // but we reload to keep the tab consistent.
      await reload();
    } catch (err) {
      console.error('Error reserving driver:', err);
    }
  };

  // Legacy activate (temporary): flips driver.status.
  // In the final model, activation should update assignments, not drivers.status.
  const handleActivate = async (id) => {
    try {
      await axios.put(`/agencies/drivers/${id}`, { status: 'Active' });
      await reload();
    } catch (err) {
      console.error('Error activating driver:', err);
    }
  };

  // Delete driver (actual entity deletion)
  const handleDeleteClick = async (id) => {
    try {
      await axios.delete(`/agencies/drivers/${id}`);
      dispatch(removeDriver(id));
      await reload();
    } catch (err) {
      console.error('Error deleting driver:', err);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading…</div>;
  }

return (
  <div className="my-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-2xl font-bold text-gray-800">Reserved Drivers</h3>

      <button
        onClick={() => setShowAddModal(true)}
        className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition"
      >
        Reserve Driver
      </button>
    </div>

    {showAddModal && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={() => setShowAddModal(false)}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <DriverForm
            initialStatus="Reserved"
            onSave={handleCreate}
            onClose={() => setShowAddModal(false)}
          />
        </div>
      </div>
    )}

    {q?.trim() && sorted.length === 0 && (
      <div className="text-sm text-gray-500 py-3">
        No matches for “{q.trim()}”.
      </div>
    )}


    <div className="overflow-x-auto">
      <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            {['Name', 'Vehicle Reg.', 'Assignment', 'Actions'].map((col) => (
              <th
                key={col}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-200">
          {sorted.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50">
            <td className="px-6 py-4 whitespace-nowrap">{row.name}</td>
            <td className="px-6 py-4 whitespace-nowrap">{row.vehicle_registration_number}</td>
            <td className="px-6 py-4 whitespace-nowrap">
              {row.assignment_status ? `${row.assignment_status} (#${row.assignment_id})` : '—'}
            </td>


              <td className="px-6 py-4">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setSelectedDriver(row)}
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Edit"
                  >
                    <Edit2 size={16} />
                  </button>

                  <button
                    onClick={() => handleActivate(row.id)}
                    className="p-1 hover:bg-gray-100 rounded text-green-600"
                    title="Activate (legacy)"
                  >
                    <RefreshCcw size={16} />
                  </button>

                  <button
                    onClick={() => handleDeleteClick(row.id)}
                    className="p-1 hover:bg-gray-100 rounded text-red-600"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {selectedDriver && (
      <DriverEditModal
        driver={selectedDriver}
        onClose={() => setSelectedDriver(null)}
        onUpdate={async () => {
          setSelectedDriver(null);
          await reload();
        }}
      />
    )}
  </div>
);
}